import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import {
  createEmptyServicePlanPayload,
  ServicePlanPayload,
} from 'src/app/models/service-plan/service-plan-payload'
import { ServicePlanStepId } from 'src/app/models/service-plan/service-plan-step-id'

// In-memory-only wizard state (ARCH3 — no save-and-resume, no localStorage/
// sessionStorage). A singleton for the app's lifetime (providedIn: 'root'), so the
// stepper shell MUST call reset(clientId) on every entry — this service does not
// reset itself just because a different clientId is passed in, and must never leak
// one client's in-progress answers into another client's session.
@Injectable({ providedIn: 'root' })
export class ServicePlanStateService {
  private payloadSubject = new BehaviorSubject<ServicePlanPayload>(
    createEmptyServicePlanPayload(),
  )
  private currentStepIdSubject = new BehaviorSubject<ServicePlanStepId>(
    'planType',
  )
  private boundClientId: string | null = null
  // Session-scoped "has the age-4+ annualSpeechPerception auto-select already run"
  // guard (story e8-6) — not part of ServicePlanPayload, since it is pure UI
  // bookkeeping with no clinical meaning, not real submission content. Must not
  // leak across clients or survive route teardown (privacy-by-design), so it is
  // reset alongside the payload in both reset() and clear().
  private annualSpeechPerceptionAutoSelectApplied = false
  // Session-scoped "has a submission been created for this session" flag (story e9-2) — set on the
  // first successful Send and on every subsequent "Submit a new plan", same non-payload UI-bookkeeping
  // precedent as annualSpeechPerceptionAutoSelectApplied above. Lets the shell's canDeactivate()
  // treat a successfully-sent plan as safe to leave, even though hasAnswers() stays true (the payload
  // is never cleared mid-session).
  private activeSubmissionCreated = false

  public payload$: Observable<ServicePlanPayload> =
    this.payloadSubject.asObservable()
  public currentStepId$: Observable<ServicePlanStepId> =
    this.currentStepIdSubject.asObservable()

  reset(clientId: string): void {
    this.boundClientId = clientId
    this.payloadSubject.next(createEmptyServicePlanPayload())
    this.currentStepIdSubject.next('planType')
    this.annualSpeechPerceptionAutoSelectApplied = false
    this.activeSubmissionCreated = false
  }

  // Called when the stepper shell is torn down (ngOnDestroy) so this root-singleton
  // service does not keep a departed client's clinical answers resident in memory
  // until the next time someone enters the route (ARCH3 / privacy-by-design).
  clear(): void {
    this.boundClientId = null
    this.payloadSubject.next(createEmptyServicePlanPayload())
    this.currentStepIdSubject.next('planType')
    this.annualSpeechPerceptionAutoSelectApplied = false
    this.activeSubmissionCreated = false
  }

  getBoundClientId(): string | null {
    return this.boundClientId
  }

  getPayload(): ServicePlanPayload {
    return this.payloadSubject.value
  }

  updatePayload(partial: Partial<ServicePlanPayload>): void {
    this.payloadSubject.next({ ...this.payloadSubject.value, ...partial })
  }

  getCurrentStepId(): ServicePlanStepId {
    return this.currentStepIdSubject.value
  }

  setCurrentStepId(stepId: ServicePlanStepId): void {
    this.currentStepIdSubject.next(stepId)
  }

  hasAppliedAnnualSpeechPerceptionAutoSelect(): boolean {
    return this.annualSpeechPerceptionAutoSelectApplied
  }

  markAnnualSpeechPerceptionAutoSelectApplied(): void {
    this.annualSpeechPerceptionAutoSelectApplied = true
  }

  hasActiveSubmission(): boolean {
    return this.activeSubmissionCreated
  }

  markSubmissionCreated(): void {
    this.activeSubmissionCreated = true
  }

  // Generic "has the clinician entered anything yet" check, used by the shell's
  // canDeactivate guard — compares against a fresh empty payload rather than a
  // hand-maintained dirty flag, so it keeps working unmodified as future e8-2+
  // stories add fields to ServicePlanPayload.
  hasAnswers(): boolean {
    return (
      JSON.stringify(this.payloadSubject.value) !==
      JSON.stringify(createEmptyServicePlanPayload())
    )
  }
}
